"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "@/api";
import Button from "@/components/common/Button";
import Card from "@/components/common/Card";
import TextArea from "@/components/common/Input/Textarea";
import TextInput from "@/components/common/Input/TextInput";
import { recordLeadTrackingSkipped, trackLeadAccepted } from "@/lib/conversion-tracking/dispatcher";
import { parseConversionEventId } from "@/lib/conversion-tracking/event-id";
import useRequestFormStore from "@/store/request/form";
import type { RequestType } from "@/types/user";
import { EventParams, trackEvent } from "@/utils/analytics";
import { debounce } from "@/utils/debounce";

interface SubmitFormProps {
  submitText?: string;
  compact?: boolean;
  submitNote?: string;
  eventParams?: EventParams;
  onSubmitAttempted?: (valid: boolean) => void;
  onSubmitted?: () => void;
}

export default function SubmitForm({
  submitText = "문의하기",
  compact = false,
  submitNote,
  eventParams,
  onSubmitAttempted,
  onSubmitted,
}: SubmitFormProps = {}) {
  const path = usePathname();
  const { form, setForm, resetForm } = useRequestFormStore();
  const submitLockRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFirstFormFocus, setIsFirstFormFocus] = useState(false);
  const [touched, setTouched] = useState({
    personName: false,
    contactNumber: false,
    email: false,
    privacy: false,
  });
  const [errors, setErrors] = useState({
    personName: "",
    contactNumber: "",
    email: "",
  });

  const [agreements, setAgreements] = useState({
    privacy: compact, // compact 모드는 버튼 클릭 = 동의 간주 (체크박스 UI 없음)
    terms: false,
  });

  const handleFirstFocus = (fieldPlaceholder: string) => {
    if (!isFirstFormFocus) {
      setIsFirstFormFocus(true);
      trackEvent("form_start", path, { first_field: fieldPlaceholder });
    }
  };

  const trackFieldProgress = debounce((field: string, fieldsCompleted: number) => {
    trackEvent("form_progress", path, {
      field_name: field,
      fields_completed: fieldsCompleted,
    });
  }, 2000);

  const handleChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(field, e.target.value);
  };

  const handleFieldBlur = (field: string, value: string, tractValueIsFilled: boolean, errorField?: keyof typeof errors) => {
    if (errorField) {
      setTouched((prev) => ({ ...prev, [errorField]: true }));
      setErrors((prev) => ({
        ...prev,
        [errorField]: validateField(errorField, form[errorField]),
      }));
    }

    if (tractValueIsFilled && value.trim()) {
      const fieldNumbers: { [field: string]: number } = {
        email: 1,
        phone: 2,
        message: 3,
      };

      trackFieldProgress(field, fieldNumbers[field] ?? 0);
    }
  };

  const handleCheckboxChange = (field: keyof typeof agreements) => {
    const newValue = !agreements[field];
    setAgreements((prev) => ({ ...prev, [field]: newValue }));
    if (field === "privacy" && newValue) {
      trackEvent("privacy_agree", path, {});
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getContactNumberDigits = (value: string) => value.replace(/\D/g, "");

  const normalizeContactNumber = (value: string) => {
    const digits = getContactNumberDigits(value);
    if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    return value.trim();
  };

  const validateContactNumber = (value: string, required = false) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return required ? "연락처를 입력해주세요" : "";
    if (/[^\d\s-]/.test(trimmedValue)) return "연락처는 숫자와 하이픈(-)만 입력해주세요";

    const digits = getContactNumberDigits(trimmedValue);
    return digits.length === 10 || digits.length === 11 ? "" : "연락처는 숫자 10~11자리로 입력해주세요";
  };

  const validatePersonName = (value: string) => {
    return value.trim() === "" ? "성함을 입력해주세요" : "";
  };

  const validateField = (field: keyof typeof errors, value: string) => {
    switch (field) {
      case "personName":
        return validatePersonName(value);
      case "contactNumber":
        return validateContactNumber(value, !compact);
      case "email":
        if (!value.trim()) return "이메일을 입력해주세요";
        return validateEmail(value) ? "" : "올바른 이메일 형식이 아닙니다";
      default:
        return "";
    }
  };

  const handleSubmit = async () => {
    if (submitLockRef.current) return;

    setTouched({ personName: true, contactNumber: true, email: true, privacy: true });
    const newErrors = {
      personName: validatePersonName(form.personName),
      contactNumber: validateContactNumber(form.contactNumber, !compact),
      email: form.email.trim() ? (validateEmail(form.email) ? "" : "올바른 이메일 형식이 아닙니다") : "이메일을 입력해주세요",
    };
    setErrors(newErrors);

    onSubmitAttempted?.(isFormValid);

    if (!isFormValid) return;

    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      const normalizedContactNumber = normalizeContactNumber(form.contactNumber);
      const entryPoint: RequestType["entryPoint"] = compact ? "home_hero" : "request_page";
      const requestData = {
        ...form,
        contactNumber: normalizedContactNumber,
        entryPoint,
        source: "00",
      };

      const res = await api.user.postRequestList(requestData);
      if (!res.success) {
        toast.error("문의 접수에 실패했습니다. 잠시 후 다시 시도해주세요.");
        return;
      }

      onSubmitted?.();

      const leadId = Number.isInteger(res.data.id) && res.data.id > 0 ? String(res.data.id) : undefined;
      const conversionEventId = parseConversionEventId(res.data.conversionEventId);
      const name = form.personName?.trim() ?? "";
      if (leadId && conversionEventId) {
        void trackLeadAccepted(
          {
            version: 1,
            leadId,
            conversionEventId,
            entryPoint,
            sourceUrl: window.location.href,
            submittedAt: new Date().toISOString(),
            consent: { analytics: "unknown", advertising: "unknown" },
            attributes: { ...eventParams, cta_type: "request" },
          },
          {
            email: form.email,
            phone: normalizedContactNumber || undefined,
            firstName: name.length > 1 ? name.slice(1) : undefined,
            lastName: name.length > 0 ? name[0] : undefined,
          },
        );
      } else {
        recordLeadTrackingSkipped(conversionEventId ?? "missing", leadId ? "missing_conversion_event_id" : "missing_lead_id");
      }

      toast.success("후보자 리스트 요청이 성공적으로 전송되었습니다!");
      resetForm();
      setAgreements({ privacy: false, terms: false });
      setIsSuccess(true);
    } catch {
      console.error("Form submission failed");
      toast.error("문의 접수에 실패했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    agreements.privacy &&
    !validatePersonName(form.personName) &&
    (compact || !validateContactNumber(form.contactNumber, true)) &&
    form.email.trim() !== "" &&
    validateEmail(form.email);

  if (isSuccess) {
    return (
      <Card className="w-full rounded-2xl border-2 border-green-100 bg-gradient-to-br from-green-50 to-white p-10 shadow-lg">
        <div className="flex flex-col items-center gap-6">
          {/* Success Icon */}
          <div className="relative">
            <div className="absolute inset-0 animate-ping rounded-full bg-green-400 opacity-30" />
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-green-600 shadow-lg">
              <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center">
            <h2 className="text-24 font-bold text-slate-900">신청이 완료되었습니다!</h2>
            <p className="mt-2 text-16 text-slate-600">감사합니다. 곧 연락드리겠습니다.</p>
          </div>

          {/* Info Cards */}
          <div className="grid w-full gap-3">
            <div className="rounded-lg bg-white p-4 text-center shadow-sm">
              <div className="flex items-center justify-center gap-2">
                <svg className="h-5 w-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-15 font-semibold text-slate-900">24시간 이내 담당자 연락</span>
              </div>
            </div>
            <div className="rounded-lg bg-white p-4 text-center shadow-sm">
              <div className="flex items-center justify-center gap-2">
                <svg className="h-5 w-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <span className="text-15 font-semibold text-slate-900">맞춤형 후보자 리스트 준비</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={
        compact
          ? "w-full rounded-xl border-slate-200 bg-white p-4 shadow-sm tablet:p-5"
          : "w-full rounded-2xl border-slate-200 bg-white p-6 shadow-lg tablet:p-8"
      }
    >
      {/* Form Header */}
      {!compact && (
        <div className="mb-5 border-b border-slate-100 pb-4">
          <h2 className="text-20 font-semibold text-slate-900 tablet:text-24">문의하기</h2>
        </div>
      )}

      <div className="space-y-4 tablet:space-y-5">
        {/* 성함 + 이메일: compact 모드는 가로 2열 grid */}
        <div className={compact ? "grid grid-cols-1 gap-4 tablet:grid-cols-2" : "space-y-4 tablet:space-y-5"}>
          <div>
            <label className="mb-2 block text-14 font-medium text-slate-700">
              성함 <span className="text-red-500">*</span>
            </label>
            <TextInput
              type="text"
              placeholder="이름 혹은 업체명"
              value={form.personName}
              onChange={handleChange("personName")}
              onFocus={() => handleFirstFocus("이름 혹은 업체명")}
              onBlur={() => handleFieldBlur("personName", form.personName, false, "personName")}
              error={touched.personName && !!errors.personName}
              errorMessage={errors.personName}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-slate-900 transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 tablet:px-4 tablet:py-3"
            />
          </div>

          <div>
            <label className="mb-2 block text-14 font-medium text-slate-700">
              이메일 <span className="text-red-500">*</span>
            </label>
            <TextInput
              type="email"
              placeholder="ex@searchright.net"
              value={form.email}
              onChange={handleChange("email")}
              onFocus={() => handleFirstFocus("ex@searchright.net")}
              onBlur={() => handleFieldBlur("email", form.email, true, "email")}
              error={touched.email && !!errors.email}
              errorMessage={errors.email}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-slate-900 transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 tablet:px-4 tablet:py-3"
            />
          </div>
        </div>

        {!compact && (
          <div>
            <label className="mb-2 block text-14 font-medium text-slate-700">
              연락처 <span className="text-red-500">*</span>
            </label>
            <TextInput
              type="tel"
              placeholder="010-0000-0000"
              value={form.contactNumber}
              onChange={handleChange("contactNumber")}
              onFocus={() => handleFirstFocus("010-0000-0000")}
              onBlur={() => handleFieldBlur("phone", form.contactNumber, true, "contactNumber")}
              error={touched.contactNumber && !!errors.contactNumber}
              errorMessage={errors.contactNumber}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-slate-900 transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 tablet:px-4 tablet:py-3"
            />
          </div>
        )}

        {!compact && (
          <div>
            <label className="mb-2 block text-14 font-medium text-slate-700">문의 내용</label>
            <div className="relative">
              <TextArea
                placeholder={"채용 관련 문의, 서비스 이용 방법, 또는 궁금하신 점이 있으시면 편하게 남겨주세요."}
                rows={3}
                value={form.text}
                onChange={handleChange("text")}
                onFocus={() => handleFirstFocus("채용 관련 문의...")}
                onBlur={() => handleFieldBlur("message", form.text, true)}
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2.5 text-slate-900 transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 tablet:px-4 tablet:py-3 tablet:rows-4"
              />
            </div>
          </div>
        )}

        {/* Agreement Section — compact 모드는 숨김 (버튼 클릭 = 동의 간주) */}
        {!compact && (
          <div className="rounded-lg bg-slate-50 p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={agreements.privacy}
                onChange={() => handleCheckboxChange("privacy")}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-sky-600 transition-colors duration-200 focus:ring-2 focus:ring-sky-500/20"
              />
              <span className="text-14 text-slate-700">
                <Link
                  href="https://searchright.notion.site/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-sky-600 underline-offset-2 hover:underline"
                >
                  개인정보 처리방침
                </Link>
                에 동의합니다 <span className="text-red-500">*</span>
              </span>
            </label>
            {touched.privacy && !agreements.privacy && (
              <p className="mt-2 text-13 text-red-600">개인정보 처리방침에 동의해주세요.</p>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="pt-2">
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="w-full rounded-lg bg-sky-600 px-6 py-3.5 text-16 font-semibold text-white shadow-md transition-all duration-200 hover:bg-sky-700 hover:shadow-lg disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                요청 중...
              </span>
            ) : (
              submitText
            )}
          </Button>
          {submitNote && <p className="mt-3 text-center text-13 text-slate-500">{submitNote}</p>}
          {compact && (
            <p className="mt-2 text-center text-12 text-slate-400">
              제출 시{" "}
              <Link
                href="https://searchright.notion.site/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-sky-600"
              >
                개인정보 처리방침
              </Link>
              에 동의한 것으로 간주됩니다.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
